import React from "react";
import Markdown from "~/components/Markdown";
import Collapsable from "~/components/Collapsable";
import { Badge, Box, Card, Link, Table, Text } from "~/components-shared";
import { CollapsableTypeEnum, TopicFootprintActionEnum, TopicFootprintTypeEnum } from "~/types/enums";
import FootprintActions from "~/components/FootprintActions";
import { truncatedContent } from "~/lib/helpers";
import styled from "styled-components";
import { t } from "i18next";
const Underline = styled.span`
  text-decoration: underline;
  font-weight: 600;
`;
const StyledTd = styled(Table.Td)`
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
`;
const StyledBox = styled(Box)`
  padding-left: 1em;
  position: relative;

  &:before {
    position: absolute;
    content: "";
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.primary.main;
}};
    border-radius: 3px;
  }
`;
const displayCustomFields = (customFields, currency) => {
  return !!customFields?.length && /*#__PURE__*/React.createElement(Card, {
    color: "transparent",
    border: true,
    fullWidth: true,
    s: {
      px: 2,
      py: 2,
      mt: 2,
      mb: 4
    }
  }, /*#__PURE__*/React.createElement(Table, {
    size: "s"
  }, /*#__PURE__*/React.createElement(Table.Body, null, customFields.map(field => {
    return field.value !== null && field.value !== undefined && /*#__PURE__*/React.createElement(Table.Tr, {
      key: field.name
    }, /*#__PURE__*/React.createElement(StyledTd, {
      noHover: true
    }, /*#__PURE__*/React.createElement(Text, {
      element: "span",
      color: "textSecondary",
      display: "inline-block"
    }, field.label, ":")), /*#__PURE__*/React.createElement(StyledTd, {
      noHover: true,
      style: {
        width: "50%"
      }
    }, field.type === "currency" && !!currency ? /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text"
    }, `${currency} ${field.value.toLocaleString("en-US")}`) : field.type === "number" ? /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text"
    }, field.value.toLocaleString("en-US")) : field.type === "boolean" ? /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text"
    }, field.value ? "Yes" : "No") : field.type === "percent" ? /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text"
    }, `${field.value.toLocaleString("en-US")}%`) : field.value.toString().length > 70 ? /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text",
      style: {
        cursor: "help",
        textDecoration: "underline dotted"
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": `<div>${field.value.toString()}</div>`
    }, truncatedContent(field.value.toString(), 70)) : /*#__PURE__*/React.createElement(Text, {
      element: "span",
      display: "block",
      weight: "semiBold",
      color: "text"
    }, field.value.toString())));
  }))));
};
const displayRelatedAccount = (record, accountName) => {
  const hasBadges = !!record.accountAnnualRevenue || !!record.accountIndustry || !!record.accountEmployees;
  return /*#__PURE__*/React.createElement(StyledBox, {
    direction: "column",
    s: {
      mt: 8,
      mb: 4,
      p: 0,
      pl: 4
    }
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Link, {
    href: record.opportunityAccountUrl,
    underline: true
  }, "Account"), " ", record.accountOwnedByName && /*#__PURE__*/React.createElement(Text, null, " ", "owned by", " ", /*#__PURE__*/React.createElement(Link, {
    href: record.accountOwnedByUrl,
    underline: true
  }, record.accountOwnedByName), " ")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4.5,
    lineHeight: 6.5,
    display: "block",
    s: {
      my: 2
    }
  }, accountName), hasBadges && /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    weight: "semiBold"
  }, !!record.accountAnnualRevenue && /*#__PURE__*/React.createElement(Badge, {
    color: "infoGrey",
    display: "inline-block",
    s: {
      py: 0.5,
      mr: 2,
      mt: -0.5,
      mb: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "Annual Revenue",
    style: {
      cursor: "help",
      textDecoration: "underline dotted"
    }
  }, record.accountCurrency, " ", record.accountAnnualRevenue.toLocaleString("en-US")), !!record.accountIndustry && /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    display: "inline-block",
    s: {
      py: 0.5,
      mr: 2,
      mt: -0.5,
      mb: 2
    }
  }, record.accountIndustry), !!record.accountEmployees && /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    display: "inline-block",
    s: {
      py: 0.5,
      mr: 2,
      mt: -0.5,
      mb: 2
    }
  }, "Employees: ", record.accountEmployees.toLocaleString("en-US"))));
};
const displayRelatedOpportunity = record => {
  const hasBadges = !!record.opportunityAmount || !!record.opportunityStageName || !!record.opportunityProbability || !!record.opportunityCloseDate;
  return /*#__PURE__*/React.createElement(StyledBox, {
    direction: "column",
    s: {
      mt: 8,
      p: 0,
      pl: 4
    }
  }, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Link, {
    href: record.taskMasterRelationUrl,
    underline: true
  }, "Opportunity"), " ", record.opportunityOwnedByName && /*#__PURE__*/React.createElement(Text, null, " ", "owned by", " ", /*#__PURE__*/React.createElement(Link, {
    href: record.opportunityOwnedByUrl,
    underline: true
  }, record.opportunityOwnedByName), " ")), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4.5,
    lineHeight: 6.5,
    display: "block",
    s: {
      my: 2
    }
  }, record.taskMasterRelationName), hasBadges && /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    weight: "semiBold"
  }, !!record.opportunityAmount && /*#__PURE__*/React.createElement(Badge, {
    color: "infoGrey",
    display: "inline-block",
    s: {
      py: 0.5,
      mr: 2,
      mt: -0.5,
      mb: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": "<div>Associated Revenue</div>",
    style: {
      cursor: "help",
      textDecoration: "underline dotted"
    }
  }, record.currency, " ", record.opportunityAmount.toLocaleString("en-US")), /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    display: "inline-block",
    s: {
      py: 0.5,
      mr: 2,
      mt: -0.5,
      mb: 2
    }
  }, record.opportunityStageName, !!record.opportunityProbability && ` | ${record.opportunityProbability.toFixed(0)}%`), !!record.opportunityCloseDate && /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    display: "inline-block",
    s: {
      py: 0.5,
      mt: -0.5,
      mb: 2
    }
  }, "Close date:", " ", new Date(record.opportunityCloseDate).toLocaleDateString("en-us", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }))));
};
const TopicFootprintContentSalesforce = _ref2 => {
  let {
    footprint,
    hideDetails = false,
    hideMessage = false
  } = _ref2;
  const {
    actions,
    type,
    data,
    aiAnalysis
  } = footprint;
  if ([TopicFootprintTypeEnum.ACCOUNT].includes(type)) {
    const {
      accountUrl,
      accountName,
      accountDescription,
      accountIndustry,
      accountEmployees,
      userOwnedByName,
      userOwnedByUrl,
      accountCurrency,
      accountAnnualRevenue,
      accountLevelRevenue,
      opportunities,
      customFields
    } = data;
    const currency = accountCurrency || "$";
    const hasAdditionalDetails = accountDescription || !!customFields?.length;
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " an", " ", /*#__PURE__*/React.createElement(Link, {
      href: accountUrl,
      underline: true
    }, "account"), " ", userOwnedByName && /*#__PURE__*/React.createElement(Text, null, "owned by", " ", /*#__PURE__*/React.createElement(Link, {
      href: userOwnedByUrl,
      underline: true
    }, userOwnedByName), " ")), /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold",
      s: {
        mt: 2
      }
    }, !!accountAnnualRevenue && /*#__PURE__*/React.createElement(Badge, {
      color: "primaryGrey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "Company Annual Revenue",
      style: {
        cursor: "help",
        textDecoration: "underline dotted"
      }
    }, currency, " ", accountAnnualRevenue.toLocaleString("en-US")), !!accountLevelRevenue && /*#__PURE__*/React.createElement(Badge, {
      color: "infoGrey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "Current Annual Recurring Revenue",
      style: {
        cursor: "help",
        textDecoration: "underline dotted"
      }
    }, "ARR: ", currency, " ", accountLevelRevenue.toLocaleString("en-US")), !!accountIndustry && /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, accountIndustry), !!accountEmployees && /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, "Employees: ", accountEmployees.toLocaleString("en-US"))), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6.5,
      display: "block",
      s: {
        my: 2
      }
    }, accountName), aiAnalysis?.summary && aiAnalysis?.summary !== "N/A" && /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      lineHeight: 6
    }, aiAnalysis.summary)), !hideMessage && hasAdditionalDetails && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3,
        mt: 3
      },
      collapsedText: "View details",
      expandedText: "Hide details"
    }, !!customFields?.length && displayCustomFields(customFields, currency), !!opportunities?.length && /*#__PURE__*/React.createElement(Text, null, "Related opportunities:", " ", opportunities.map((opportunity, index) => {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        href: opportunity.url
      }, opportunity.name), index < opportunities.length ? ", " : ".");
    })), /*#__PURE__*/React.createElement(Markdown, {
      text: accountDescription,
      fontSize: 4,
      lineHeight: 6,
      display: "block",
      s: {
        mt: 3
      }
    })));
  }
  if ([TopicFootprintTypeEnum.OPPORTUNITY].includes(type)) {
    const {
      opportunityUrl,
      opportunityName,
      userOwnedByName,
      userOwnedByUrl,
      opportunityAccountName,
      opportunityStageName,
      opportunityProbability,
      opportunityDescription,
      opportunityCurrency,
      opportunityPreviousStageName,
      opportunityPreviousAmount,
      opportunityPreviousProbability,
      opportunityPreviousCloseDate,
      opportunityAmount,
      opportunityLevelRevenue,
      opportunityCloseDate
    } = data;
    const customFields = data.customFields ? data.customFields.filter((obj, index, self) => index === self.findIndex(o => o.name === obj.name)) : [];
    const amount = opportunityAmount ?? opportunityLevelRevenue;
    const currency = opportunityCurrency || "USD";
    const stageChanged = actions.includes(TopicFootprintActionEnum.CHANGED_STAGE);
    const amountChanged = actions.includes(TopicFootprintActionEnum.CHANGED_AMOUNT);
    const probabilityChanged = actions.includes(TopicFootprintActionEnum.CHANGED_PROBABILITY);
    const closeDateChanged = actions.includes(TopicFootprintActionEnum.CHANGED_CLOSE_DATE);
    const hasAdditionalDetails = opportunityDescription || stageChanged || amountChanged || !!customFields?.length;
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " an", " ", /*#__PURE__*/React.createElement(Link, {
      href: opportunityUrl,
      underline: true
    }, "opportunity"), " ", userOwnedByName && /*#__PURE__*/React.createElement(Text, null, "owned by", " ", /*#__PURE__*/React.createElement(Link, {
      href: userOwnedByUrl,
      underline: true
    }, userOwnedByName), " ")), /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold",
      s: {
        mt: 2
      }
    }, amount != null && /*#__PURE__*/React.createElement(Badge, {
      color: "primaryGrey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-html": "<div>Associated Revenue</div>",
      style: {
        cursor: "help",
        textDecoration: "underline dotted"
      }
    }, currency, " ", amount.toLocaleString("en-US")), /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, opportunityStageName, !!opportunityProbability && ` | ${opportunityProbability.toFixed(0)}%`), !!opportunityCloseDate && /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mt: -0.5,
        mb: 2
      }
    }, "Close date:", " ", new Date(opportunityCloseDate).toLocaleDateString("en-us", {
      year: "numeric",
      month: "short",
      day: "numeric"
    }))), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6.5,
      display: "block",
      s: {
        my: 2
      }
    }, opportunityName), aiAnalysis?.summary && /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      lineHeight: 6
    }, aiAnalysis.summary), aiAnalysis?.changes && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4,
      lineHeight: 6.5,
      display: "block",
      s: {
        mt: 5,
        mb: 1
      }
    }, t("TopicFootprintContentSalesforce.whatHasChanged")), /*#__PURE__*/React.createElement(Collapsable, null, /*#__PURE__*/React.createElement(Markdown, {
      s: {
        mb: 1
      },
      fontSize: 4,
      lineHeight: 6,
      display: "block",
      text: aiAnalysis.changes
    })))), !hideMessage && hasAdditionalDetails && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3,
        mt: 3
      },
      collapsedText: "View details",
      expandedText: "Hide details"
    }, stageChanged && !!opportunityPreviousStageName && !!opportunityStageName && /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      lineHeight: 6
    }, "- Stage changed from ", /*#__PURE__*/React.createElement(Underline, null, opportunityPreviousStageName), " to", " ", /*#__PURE__*/React.createElement(Underline, null, opportunityStageName)), amountChanged && !!opportunityPreviousAmount && !!opportunityAmount && /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      lineHeight: 6
    }, "- Associated revenue changed from", " ", /*#__PURE__*/React.createElement(Underline, null, currency, " ", opportunityPreviousAmount.toLocaleString("en-US")), " ", "to", " ", /*#__PURE__*/React.createElement(Underline, null, currency, " ", opportunityAmount.toLocaleString("en-US"))), probabilityChanged && !!opportunityPreviousProbability && !!opportunityProbability && /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      lineHeight: 6
    }, "- Probability changed from ", /*#__PURE__*/React.createElement(Underline, null, opportunityPreviousProbability, "%"), " to", " ", /*#__PURE__*/React.createElement(Underline, null, opportunityProbability, "%")), closeDateChanged && !!opportunityPreviousCloseDate && !!opportunityCloseDate && /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      lineHeight: 6
    }, "- Close date changed from", " ", /*#__PURE__*/React.createElement(Underline, null, new Date(opportunityPreviousCloseDate).toLocaleDateString("en-us", {
      year: "numeric",
      month: "short",
      day: "numeric"
    })), " ", "to", " ", /*#__PURE__*/React.createElement(Underline, null, new Date(opportunityCloseDate).toLocaleDateString("en-us", {
      year: "numeric",
      month: "short",
      day: "numeric"
    }))), !!customFields?.length && displayCustomFields(customFields, currency), /*#__PURE__*/React.createElement(Markdown, {
      text: opportunityDescription,
      fontSize: 4,
      lineHeight: 6,
      display: "block",
      s: {
        mt: 3
      }
    })), displayRelatedAccount(data, opportunityAccountName));
  }
  if ([TopicFootprintTypeEnum.EMAIL].includes(type)) {
    const {
      emailMessageUrl,
      emailMessageSubject,
      emailMessageContent,
      emailMessageContentStructured,
      emailMessageFrom,
      emailMessageTo,
      emailMessageIncoming,
      opportunityName
    } = data;
    const limitedStructuredContent = truncatedContent(emailMessageContentStructured.currentMessage, 130);
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold",
      lineHeight: 6
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " ", /*#__PURE__*/React.createElement(Link, {
      href: emailMessageUrl,
      underline: true
    }, "email"), " ", emailMessageIncoming ? /*#__PURE__*/React.createElement(Text, null, "from ", emailMessageFrom) : /*#__PURE__*/React.createElement(Text, null, "to ", emailMessageTo), !!emailMessageSubject.length && /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline",
      s: {
        py: 0.5,
        mx: 2,
        mt: -0.5
      }
    }, emailMessageSubject)), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6.5,
      display: "block",
      s: {
        my: 1
      }
    }, opportunityName), /*#__PURE__*/React.createElement(Text, {
      fontSize: 3,
      lineHeight: 4.5,
      weight: "bold",
      display: "block",
      s: {
        mt: 2
      }
    }, emailMessageFrom, " wrote:"), /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      lineHeight: 6,
      s: {
        mt: 3
      }
    }, limitedStructuredContent)), !hideMessage && emailMessageContent && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        my: 3
      },
      collapsedText: "View full content",
      expandedText: "Hide full content"
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: emailMessageContent,
      fontSize: 3.5,
      lineHeight: 4.5,
      display: "block"
    })));
  }
  if ([TopicFootprintTypeEnum.TASK].includes(type)) {
    const {
      taskUrl,
      taskSubject,
      taskDescription,
      taskStatus,
      taskPriority,
      taskIsHighPriority,
      taskType,
      taskSubtype,
      taskMasterRelationName,
      taskMasterRelationObject,
      taskMasterRelationUrl,
      customFields
    } = data;
    const isEmail = taskType === "Email" || taskSubtype === "Email";
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold",
      lineHeight: 6
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " a", " ", /*#__PURE__*/React.createElement(Link, {
      href: taskUrl,
      display: "inline",
      underline: true
    }, "task"), !!taskMasterRelationUrl && !!taskMasterRelationName && !!taskMasterRelationObject && /*#__PURE__*/React.createElement(React.Fragment, null, " ", "related to ", taskMasterRelationObject.toLowerCase(), " ", /*#__PURE__*/React.createElement(Link, {
      href: taskMasterRelationUrl,
      display: "inline",
      underline: true
    }, taskMasterRelationName)), /*#__PURE__*/React.createElement(Text, {
      s: {
        ml: 2
      }
    }, /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, taskType), !isEmail && /*#__PURE__*/React.createElement(Badge, {
      color: "grey",
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, taskStatus), (!isEmail || isEmail && taskIsHighPriority) && /*#__PURE__*/React.createElement(Badge, {
      color: `${taskIsHighPriority ? "translucentDanger" : "grey"}`,
      display: "inline-block",
      s: {
        py: 0.5,
        mr: 2,
        mt: -0.5,
        mb: 2
      }
    }, "Priority: ", taskPriority))), aiAnalysis?.summary && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4,
      lineHeight: 6.5,
      display: "block",
      s: {
        mb: 1
      }
    }, t("TopicFootprintContentSalesforce.summary")), /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      lineHeight: 6
    }, aiAnalysis.summary))), !hideMessage && (!!taskSubject.length || !!taskDescription.length) && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 120,
      s: {
        my: 4
      }
    }, !!taskSubject.length && /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "medium",
      display: "block",
      s: {
        py: 0.5,
        mr: 2,
        mb: 3
      }
    }, "Subject:", /*#__PURE__*/React.createElement(Text, {
      color: "text",
      weight: "bold",
      display: "inline",
      s: {
        ml: 1
      }
    }, taskSubject)), /*#__PURE__*/React.createElement(Markdown, {
      text: taskDescription,
      fontSize: 3.5,
      lineHeight: 4.5,
      display: "block"
    })), !!customFields?.length && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        my: 2
      },
      collapsedText: "View custom fields",
      expandedText: "Hide custom fields"
    }, displayCustomFields(customFields)), taskMasterRelationObject === "Account" ? displayRelatedAccount(data, taskMasterRelationName) : taskMasterRelationObject === "Opportunity" && displayRelatedOpportunity(data));
  }
  return /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold"
  }, "Unknown event");
};
export default TopicFootprintContentSalesforce;